import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from './Header.tsx';
import useTheme from '../hooks/useTheme.ts';
import { WorkflowProvider, useWorkflow, WORKFLOW_STEPS } from './workflow/WorkflowContext.tsx';
import Pads from './workflow/Pads.tsx';
import WellConfig from './workflow/WellConfig.tsx';
import FracSchedule from './workflow/FracSchedule.tsx';
import PadDeploy from './workflow/PadDeploy.tsx';
import {
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiChevronRight,
  FiCheckCircle, FiClock, FiAlertCircle, FiArrowLeft,
  FiGrid, FiList, FiMoreVertical, FiTarget, FiX, FiLoader,
} from 'react-icons/fi';
import axios from 'axios';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// ============================================
// TYPES
// ============================================

interface ApiPad {
  id: number;
  job_number: number;
  pad_name: string;
  organization_id: number;
  state: string | null;
  county: string | null;
  fleet: number | null;
  afe: string | null;
  status: string | null;
  well_count: number | null;
  stage_count: number | null;
  start_ts: number | null;
  end_ts: number | null;
}

interface Organization {
  id: number;
  name: string;
}

// ============================================
// PLAN CONTENT (inside WorkflowProvider)
// ============================================

const PlanContent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const {
    state: workflowState,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    resetWorkflow,
    validateStep,
    loadPadFromApi,
  } = useWorkflow();
  const { currentStep, isLoading: workflowLoading } = workflowState;
  const isViewer = !(JSON.parse(localStorage.getItem('roles') || '[]') as string[])
    .some(r => r === 'Manager' || r === 'Editor');

  // ── View state ──────────────────────────────
  const [view, setView] = useState<'list' | 'new' | 'edit'>('list');
  const [editingPadId, setEditingPadId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);

  // ── API data ────────────────────────────────
  const [pads, setPads] = useState<ApiPad[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Fetch helpers ───────────────────────────
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchPads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [padsRes, orgsRes] = await Promise.all([
        axios.get(`${backendUrl}/pads`, { headers: getAuthHeaders() }),
        axios.get(`${backendUrl}/organizations/list`, { headers: getAuthHeaders() }),
      ]);
      setPads(padsRes.data || []);
      setOrganizations(orgsRes.data || []);
    } catch (err: any) {
      console.error('Failed to fetch pads:', err);
      setError('Failed to load pads. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch on mount
  useEffect(() => {
    fetchPads();
  }, [fetchPads]);

  // Handle URL params on mount
  useEffect(() => {
    const action = searchParams.get('action');
    const padParam = searchParams.get('pad');

    if (action === 'new') {
      startNewWorkflow();
    } else if (action === 'edit' && padParam) {
      editPad(parseInt(padParam, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Org name lookup ─────────────────────────
  const getOrgName = useCallback((orgId: number): string => {
    return organizations.find(o => o.id === orgId)?.name || '';
  }, [organizations]);

  // ── Pad status mapping ──────────────────────
  const getPadDisplayStatus = (pad: ApiPad): string => {
    if (!pad.status) return 'Draft';
    const st = pad.status.toLowerCase();
    if (st === 'planned' && (pad.well_count || 0) === 0) return 'Draft';
    if (st === 'planned' && (pad.well_count || 0) > 0) return 'Configured';
    if (st === 'active') return 'Active';
    if (st === 'completed') return 'Completed';
    return pad.status;
  };

  // ── Filtering & sorting ─────────────────────
  const filteredPads = useMemo(() => {
    return pads.filter(pad => {
      const displayStatus = getPadDisplayStatus(pad);
      const orgName = getOrgName(pad.organization_id);
      const matchesSearch =
        (pad.pad_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        orgName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pad.state || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' ||
        displayStatus.toLowerCase() === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [pads, searchTerm, statusFilter, getOrgName]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: pads.length };
    pads.forEach(p => {
      const st = getPadDisplayStatus(p);
      counts[st] = (counts[st] || 0) + 1;
    });
    return counts;
  }, [pads]);

  const statusLabels = useMemo(() => {
    const labels = new Set<string>();
    pads.forEach(p => labels.add(getPadDisplayStatus(p)));
    return ['all', ...Array.from(labels).sort()];
  }, [pads]);

  // ── Actions ─────────────────────────────────
  const startNewWorkflow = () => {
    resetWorkflow();
    setView('new');
    setCurrentStep(0);
    setSearchParams({ action: 'new' });
  };

  const editPad = async (padId: number) => {
    setEditingPadId(padId);
    resetWorkflow();
    setView('edit');
    setCurrentStep(0);
    setSearchParams({ action: 'edit', pad: String(padId) });
    await loadPadFromApi(padId);
  };

  const backToList = () => {
    setView('list');
    setEditingPadId(null);
    resetWorkflow();
    setSearchParams({});
    fetchPads();
  };

  const deletePad = async (padId: number) => {
    if (!window.confirm('Are you sure you want to delete this pad? This will also delete all wells, stages, and associated data.')) {
      setMenuOpenId(null);
      return;
    }
    try {
      await axios.delete(`${backendUrl}/pads/${padId}`, { headers: getAuthHeaders() });
      setPads(prev => prev.filter(p => p.id !== padId));
    } catch (err) {
      console.error('Failed to delete pad:', err);
      alert('Failed to delete pad. You may not have permission.');
    }
    setMenuOpenId(null);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handler = () => setMenuOpenId(null);
    if (menuOpenId !== null) {
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [menuOpenId]);

  // ── Status helpers ──────────────────────────
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft': return '#9CA3AF';
      case 'Configured': return '#3E76FE';
      case 'Active': return '#F9C844';
      case 'Completed': return '#8CC540';
      default: return '#9CA3AF';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Draft': return <FiEdit2 size={14} />;
      case 'Configured': return <FiCheckCircle size={14} />;
      case 'Active': return <FiClock size={14} />;
      case 'Completed': return <FiTarget size={14} />;
      default: return <FiAlertCircle size={14} />;
    }
  };

  const getProgressPercent = (status: string) => {
    switch (status) {
      case 'Draft': return 15;
      case 'Configured': return 50;
      case 'Active': return 75;
      case 'Completed': return 100;
      default: return 0;
    }
  };

  // ── Derived values for workflow view ─────────
  const progressWidth = (currentStep / (WORKFLOW_STEPS.length - 1)) * 80;
  const isLastStep = currentStep === WORKFLOW_STEPS.length - 1;
  const canProceed = validateStep(currentStep);
  const editingPad = pads.find(p => p.id === editingPadId);

  const handleBack = () => {
    if (currentStep > 0) goToPreviousStep();
    else backToList();
  };

  const handleNext = () => {
    if (canProceed) goToNextStep();
  };

  // ── Styles ──────────────────────────────────
  const s = {
    page: { minHeight: '100vh', backgroundColor: isDark ? '#1A1D1F' : '#F5F7FA', color: isDark ? '#E5E7EB' : '#1A1D1F', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' } as React.CSSProperties,
    content: { maxWidth: '1400px', margin: '0 auto', padding: '32px 40px' } as React.CSSProperties,
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' } as React.CSSProperties,
    titleGroup: { display: 'flex', flexDirection: 'column' as const, gap: '4px' } as React.CSSProperties,
    pageTitle: { fontSize: '28px', fontWeight: 700, margin: 0, letterSpacing: '-0.5px' } as React.CSSProperties,
    pageSubtitle: { fontSize: '14px', color: isDark ? '#9CA3AF' : '#6B7280', margin: 0 } as React.CSSProperties,
    newBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#8CC540', color: '#FFFFFF', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' } as React.CSSProperties,
    filtersRow: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap' as const } as React.CSSProperties,
    searchBox: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '8px', border: `1px solid ${isDark ? '#303335' : '#E0E0E0'}`, backgroundColor: isDark ? '#232629' : '#FFFFFF', flex: '1', maxWidth: '360px' } as React.CSSProperties,
    searchInput: { border: 'none', background: 'transparent', outline: 'none', fontSize: '14px', color: isDark ? '#E5E7EB' : '#1A1D1F', width: '100%' } as React.CSSProperties,
    statusPill: (active: boolean, color?: string) => ({ padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', border: `1px solid ${active ? (color || '#8CC540') : (isDark ? '#303335' : '#E0E0E0')}`, backgroundColor: active ? `${color || '#8CC540'}15` : 'transparent', color: active ? (color || '#8CC540') : (isDark ? '#9CA3AF' : '#6B7280') }) as React.CSSProperties,
    viewToggle: { display: 'flex', gap: '4px', padding: '4px', borderRadius: '8px', backgroundColor: isDark ? '#232629' : '#F0F0F0', marginLeft: 'auto' } as React.CSSProperties,
    viewBtn: (active: boolean) => ({ padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: active ? (isDark ? '#303335' : '#FFFFFF') : 'transparent', color: active ? (isDark ? '#FFFFFF' : '#1A1D1F') : (isDark ? '#6B7280' : '#9CA3AF'), display: 'flex', alignItems: 'center', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }) as React.CSSProperties,
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' } as React.CSSProperties,
    card: { backgroundColor: isDark ? '#232629' : '#FFFFFF', borderRadius: '12px', border: `1px solid ${isDark ? '#303335' : '#E0E0E0'}`, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s' } as React.CSSProperties,
    cardHeader: { padding: '20px 20px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' } as React.CSSProperties,
    cardTitle: { fontSize: '16px', fontWeight: 600, margin: '0 0 4px 0' } as React.CSSProperties,
    cardOperator: { fontSize: '13px', color: isDark ? '#9CA3AF' : '#6B7280' } as React.CSSProperties,
    statusBadge: (color: string) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, backgroundColor: `${color}15`, color }) as React.CSSProperties,
    cardBody: { padding: '0 20px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' } as React.CSSProperties,
    cardStat: { display: 'flex', flexDirection: 'column' as const, gap: '2px' } as React.CSSProperties,
    cardStatLabel: { fontSize: '11px', textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: isDark ? '#6B7280' : '#9CA3AF', fontWeight: 500 } as React.CSSProperties,
    cardStatValue: { fontSize: '14px', fontWeight: 600 } as React.CSSProperties,
    cardProgress: { padding: '0 20px 12px' } as React.CSSProperties,
    progressTrack: { height: '4px', borderRadius: '2px', backgroundColor: isDark ? '#303335' : '#E5E7EB', overflow: 'hidden' } as React.CSSProperties,
    progressFill: (percent: number, color: string) => ({ height: '100%', width: `${percent}%`, borderRadius: '2px', backgroundColor: color, transition: 'width 0.4s ease' }) as React.CSSProperties,
    cardFooter: { padding: '12px 20px', borderTop: `1px solid ${isDark ? '#303335' : '#E0E0E0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: isDark ? '#6B7280' : '#9CA3AF' } as React.CSSProperties,
    cardAction: { display: 'flex', alignItems: 'center', gap: '4px', color: '#3E76FE', fontWeight: 600, fontSize: '13px' } as React.CSSProperties,
    listRow: { display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 40px', gap: '16px', alignItems: 'center', padding: '16px 20px', backgroundColor: isDark ? '#232629' : '#FFFFFF', borderBottom: `1px solid ${isDark ? '#303335' : '#E0E0E0'}`, cursor: 'pointer', transition: 'background 0.15s' } as React.CSSProperties,
    listHeader: { display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 40px', gap: '16px', padding: '12px 20px', fontSize: '11px', textTransform: 'uppercase' as const, letterSpacing: '0.5px', fontWeight: 600, color: isDark ? '#6B7280' : '#9CA3AF', backgroundColor: isDark ? '#1A1D1F' : '#F5F7FA', borderRadius: '8px 8px 0 0', borderBottom: `1px solid ${isDark ? '#303335' : '#E0E0E0'}` } as React.CSSProperties,
    listContainer: { borderRadius: '12px', border: `1px solid ${isDark ? '#303335' : '#E0E0E0'}`, overflow: 'hidden' } as React.CSSProperties,
    workflowCard: { backgroundColor: isDark ? '#232629' : '#FFFFFF', borderRadius: '12px', border: `1px solid ${isDark ? '#303335' : '#E0E0E0'}`, padding: '24px' } as React.CSSProperties,
    backBtn: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', border: `1px solid ${isDark ? '#303335' : '#E0E0E0'}`, borderRadius: '8px', backgroundColor: 'transparent', color: isDark ? '#E5E7EB' : '#1A1D1F', fontSize: '14px', cursor: 'pointer', marginBottom: '24px' } as React.CSSProperties,
    progressBar: { display: 'flex', justifyContent: 'space-between', position: 'relative' as const, marginBottom: '32px', padding: '0 20px' } as React.CSSProperties,
    progressLine: { position: 'absolute' as const, top: '20px', left: '10%', right: '10%', height: '3px', backgroundColor: isDark ? '#303335' : '#E5E7EB', zIndex: 0 } as React.CSSProperties,
    progressLineFill: { position: 'absolute' as const, top: '20px', left: '10%', height: '3px', backgroundColor: '#8CC540', zIndex: 1, transition: 'width 0.3s ease' } as React.CSSProperties,
    stepItem: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: '8px', zIndex: 2, flex: 1 } as React.CSSProperties,
    stepCircle: { width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 600, transition: 'all 0.2s' } as React.CSSProperties,
    stepTitle: { fontSize: '13px', fontWeight: 600, textAlign: 'center' as const } as React.CSSProperties,
    stepDesc: { fontSize: '11px', color: isDark ? '#6B7280' : '#9CA3AF', textAlign: 'center' as const } as React.CSSProperties,
    navRow: { display: 'flex', justifyContent: 'space-between', marginTop: '24px' } as React.CSSProperties,
    btn: { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'all 0.15s' } as React.CSSProperties,
    btnPrimary: { backgroundColor: '#8CC540', color: '#FFFFFF' } as React.CSSProperties,
    btnSecondary: { backgroundColor: isDark ? '#303335' : '#E5E7EB', color: isDark ? '#E5E7EB' : '#1A1D1F' } as React.CSSProperties,
    emptyState: { textAlign: 'center' as const, padding: '80px 20px' } as React.CSSProperties,
    actionMenu: { position: 'absolute' as const, right: '0', top: '100%', zIndex: 100, minWidth: '160px', padding: '8px 0', borderRadius: '8px', backgroundColor: isDark ? '#232629' : '#FFFFFF', border: `1px solid ${isDark ? '#303335' : '#E0E0E0'}`, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } as React.CSSProperties,
    actionItem: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: isDark ? '#E5E7EB' : '#1A1D1F', border: 'none', width: '100%', backgroundColor: 'transparent', textAlign: 'left' as const } as React.CSSProperties,
    loadingCenter: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', flexDirection: 'column' as const, gap: '16px', color: isDark ? '#9CA3AF' : '#6B7280' } as React.CSSProperties,
  };

  // ══════════════════════════════════════════════
  // RENDER — All content inlined directly (no inline component functions!)
  // This is critical: defining PadListView/WorkflowView as arrow functions
  // inside PlanContent caused React to unmount/remount children on every
  // re-render, destroying local state in WellConfig, Pads, etc.
  // ══════════════════════════════════════════════
  return (
    <div style={s.page}>
      <Header title="Machina" />
      <div style={s.content}>

        {/* ──────────────────────────────────────── */}
        {/* PAD LIST VIEW                            */}
        {/* ──────────────────────────────────────── */}
        {view === 'list' && (
          <>
            {loading ? (
              <div style={s.loadingCenter}>
                <FiLoader size={32} style={{ animation: 'spin 1s linear infinite' }} />
                <span>Loading pads...</span>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : error ? (
              <div style={s.loadingCenter}>
                <FiAlertCircle size={32} color="#EF4444" />
                <span>{error}</span>
                <button style={{ ...s.btn, ...s.btnPrimary }} onClick={fetchPads}>Retry</button>
              </div>
            ) : (
              <>
                {/* Header */}
                <div style={s.topBar}>
                  <div style={s.titleGroup}>
                    <h1 style={s.pageTitle}>Plan</h1>
                    <p style={s.pageSubtitle}>Manage pad configurations, well setups, and job deployments</p>
                  </div>
                  {!isViewer && (
                    <button style={s.newBtn} onClick={startNewWorkflow}>
                      <FiPlus size={16} /> New Pad
                    </button>
                  )}
                </div>

                {/* Filters */}
                <div style={s.filtersRow}>
                  <div style={s.searchBox}>
                    <FiSearch size={16} color={isDark ? '#6B7280' : '#9CA3AF'} />
                    <input
                      style={s.searchInput}
                      placeholder="Search pads, operators, states..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <FiX size={14} style={{ cursor: 'pointer', color: '#9CA3AF' }} onClick={() => setSearchTerm('')} />
                    )}
                  </div>

                  {statusLabels.map(status => (
                    <button
                      key={status}
                      style={s.statusPill(
                        statusFilter === (status === 'all' ? 'all' : status.toLowerCase()),
                        status !== 'all' ? getStatusColor(status) : undefined,
                      )}
                      onClick={() => setStatusFilter(status === 'all' ? 'all' : status.toLowerCase())}
                    >
                      {status === 'all' ? `All (${statusCounts.all || 0})` : `${status} (${statusCounts[status] || 0})`}
                    </button>
                  ))}

                  <div style={s.viewToggle}>
                    <button style={s.viewBtn(viewMode === 'grid')} onClick={() => setViewMode('grid')}><FiGrid size={16} /></button>
                    <button style={s.viewBtn(viewMode === 'list')} onClick={() => setViewMode('list')}><FiList size={16} /></button>
                  </div>
                </div>

                {/* Pad Cards / List / Empty */}
                {filteredPads.length === 0 ? (
                  <div style={s.emptyState}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                    <div style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
                      {searchTerm || statusFilter !== 'all' ? 'No matching pads' : 'No pads yet'}
                    </div>
                    <div style={{ fontSize: '14px', color: isDark ? '#6B7280' : '#9CA3AF', marginBottom: '24px' }}>
                      {searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filters' : 'Create your first pad to get started'}
                    </div>
                    {!searchTerm && statusFilter === 'all' && !isViewer && (
                      <button style={{ ...s.btn, ...s.btnPrimary }} onClick={startNewWorkflow}>
                        <FiPlus size={16} /> Create First Pad
                      </button>
                    )}
                  </div>
                ) : viewMode === 'grid' ? (
                  /* ── Grid View ── */
                  <div style={s.grid}>
                    {filteredPads.map(pad => {
                      const displayStatus = getPadDisplayStatus(pad);
                      const statusColor = getStatusColor(displayStatus);
                      const progress = getProgressPercent(displayStatus);
                      const orgName = getOrgName(pad.organization_id);

                      return (
                        <div
                          key={pad.id}
                          style={s.card}
                          onClick={() => editPad(pad.id)}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLDivElement).style.borderColor = statusColor;
                            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                            (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 24px ${statusColor}15`;
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLDivElement).style.borderColor = isDark ? '#303335' : '#E0E0E0';
                            (e.currentTarget as HTMLDivElement).style.transform = 'none';
                            (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                          }}
                        >
                          <div style={s.cardHeader}>
                            <div>
                              <div style={s.cardTitle}>{pad.pad_name}</div>
                              <div style={s.cardOperator}>{orgName}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={s.statusBadge(statusColor)}>
                                {getStatusIcon(displayStatus)} {displayStatus}
                              </span>
                              <div style={{ position: 'relative' }}>
                                <button
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#6B7280' : '#9CA3AF', padding: '4px' }}
                                  onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === pad.id ? null : pad.id); }}
                                >
                                  <FiMoreVertical size={16} />
                                </button>
                                {menuOpenId === pad.id && (
                                  <div style={s.actionMenu}>
                                    <button
                                      style={s.actionItem}
                                      onClick={(e) => { e.stopPropagation(); editPad(pad.id); }}
                                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = isDark ? '#303335' : '#F5F7FA'; }}
                                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
                                    >
                                      <FiEdit2 size={14} /> Edit Configuration
                                    </button>
                                    <button
                                      style={{ ...s.actionItem, color: '#EF4444' }}
                                      onClick={(e) => { e.stopPropagation(); deletePad(pad.id); }}
                                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = isDark ? '#303335' : '#F5F7FA'; }}
                                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
                                    >
                                      <FiTrash2 size={14} /> Delete Pad
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div style={s.cardBody}>
                            <div style={s.cardStat}>
                              <span style={s.cardStatLabel}>Wells</span>
                              <span style={s.cardStatValue}>{pad.well_count || 0}</span>
                            </div>
                            <div style={s.cardStat}>
                              <span style={s.cardStatLabel}>Total Stages</span>
                              <span style={s.cardStatValue}>{pad.stage_count || '—'}</span>
                            </div>
                            <div style={s.cardStat}>
                              <span style={s.cardStatLabel}>Location</span>
                              <span style={s.cardStatValue}>
                                {pad.county ? `${pad.county}, ` : ''}{pad.state || '—'}
                              </span>
                            </div>
                            <div style={s.cardStat}>
                              <span style={s.cardStatLabel}>Job #</span>
                              <span style={s.cardStatValue}>{pad.job_number}</span>
                            </div>
                          </div>

                          <div style={s.cardProgress}>
                            <div style={s.progressTrack}>
                              <div style={s.progressFill(progress, statusColor)} />
                            </div>
                          </div>

                          <div style={s.cardFooter}>
                            <span>{pad.afe ? `AFE: ${pad.afe}` : `Fleet: ${pad.fleet || '—'}`}</span>
                            <span style={s.cardAction}>
                              {displayStatus === 'Draft' ? 'Continue Setup' : 'View & Edit'} <FiChevronRight size={14} />
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* ── List View ── */
                  <div style={s.listContainer}>
                    <div style={s.listHeader}>
                      <span>Pad / Operator</span>
                      <span>Location</span>
                      <span>Wells</span>
                      <span>Stages</span>
                      <span>Status</span>
                      <span></span>
                    </div>
                    {filteredPads.map(pad => {
                      const displayStatus = getPadDisplayStatus(pad);
                      const statusColor = getStatusColor(displayStatus);
                      const orgName = getOrgName(pad.organization_id);
                      return (
                        <div
                          key={pad.id}
                          style={s.listRow}
                          onClick={() => editPad(pad.id)}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = isDark ? '#1A1D1F' : '#F5F7FA'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = isDark ? '#232629' : '#FFFFFF'; }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '14px' }}>{pad.pad_name}</div>
                            <div style={{ fontSize: '12px', color: isDark ? '#6B7280' : '#9CA3AF' }}>{orgName}</div>
                          </div>
                          <div style={{ fontSize: '13px' }}>{pad.county ? `${pad.county}, ` : ''}{pad.state || '—'}</div>
                          <div style={{ fontSize: '14px', fontWeight: 500 }}>{pad.well_count || 0}</div>
                          <div style={{ fontSize: '14px', fontWeight: 500 }}>{pad.stage_count || '—'}</div>
                          <span style={s.statusBadge(statusColor)}>{getStatusIcon(displayStatus)} {displayStatus}</span>
                          <FiChevronRight size={16} color={isDark ? '#6B7280' : '#9CA3AF'} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ──────────────────────────────────────── */}
        {/* WORKFLOW VIEW (new or edit)               */}
        {/* ──────────────────────────────────────── */}
        {(view === 'new' || view === 'edit') && (
          <>
            {workflowLoading ? (
              <>
                <button style={s.backBtn} onClick={backToList}>
                  <FiArrowLeft size={16} /> Back to Plan
                </button>
                <div style={s.loadingCenter}>
                  <FiLoader size={32} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>Loading pad configuration...</span>
                  <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </div>
              </>
            ) : (
              <>
                <button style={s.backBtn} onClick={backToList}>
                  <FiArrowLeft size={16} /> Back to Plan
                </button>

                <div style={{ marginBottom: '24px' }}>
                  <h1 style={{ ...s.pageTitle, fontSize: '24px' }}>
                    {view === 'new' ? 'New Pad Setup' : `Edit: ${editingPad?.pad_name || workflowState.padData.padName || 'Pad'}`}
                  </h1>
                  <p style={s.pageSubtitle}>
                    {view === 'new' ? 'Follow the steps to configure and deploy a new job' : 'Modify pad configuration, wells, schedule, and deployment'}
                  </p>
                </div>

                {/* Progress Steps */}
                <div style={s.progressBar}>
                  <div style={s.progressLine} />
                  <div style={{ ...s.progressLineFill, width: `${progressWidth}%` }} />
                  {WORKFLOW_STEPS.map((step, i) => (
                    <div key={step.id} style={s.stepItem}>
                      <div
                        style={{
                          ...s.stepCircle,
                          backgroundColor: i <= currentStep ? '#8CC540' : (isDark ? '#303335' : '#E5E7EB'),
                          color: i <= currentStep ? '#FFFFFF' : (isDark ? '#6B7280' : '#9CA3AF'),
                          border: i === currentStep ? '3px solid #F9C844' : 'none',
                          cursor: i < currentStep ? 'pointer' : 'default',
                        }}
                        onClick={() => { if (i < currentStep) setCurrentStep(i); }}
                      >
                        {i < currentStep ? '✓' : step.icon}
                      </div>
                      <div style={{ ...s.stepTitle, color: i <= currentStep ? (isDark ? '#FFFFFF' : '#1A1D1F') : '#6B7280' }}>
                        {step.title}
                      </div>
                      <div style={s.stepDesc}>{step.desc}</div>
                    </div>
                  ))}
                </div>

                {/* Workflow Step Content */}
                <div style={s.workflowCard}>
                  {currentStep === 0 && <Pads isViewer={isViewer} />}
                  {currentStep === 1 && <WellConfig isViewer={isViewer} />}
                  {currentStep === 2 && <FracSchedule isViewer={isViewer} />}
                  {currentStep === 3 && (
                    <PadDeploy
                      onEdit={(step: number) => setCurrentStep(step)}
                      onDeploy={() => backToList()}
                      isViewer={isViewer}
                    />
                  )}
                </div>

                {/* Navigation — hide Deploy button on last step (PadDeploy has its own) */}
                <div style={s.navRow}>
                  <button style={{ ...s.btn, ...s.btnSecondary }} onClick={handleBack}>
                    ← {currentStep > 0 ? 'Previous' : 'Back to Plan'}
                  </button>
                  {!isLastStep && (
                    <button
                      style={{ ...s.btn, ...s.btnPrimary, opacity: canProceed ? 1 : 0.6 }}
                      onClick={handleNext}
                      disabled={!canProceed}
                    >
                      Next Step →
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
};

// ============================================
// PLAN PAGE (wraps content in WorkflowProvider)
// ============================================

const Plan: React.FC = () => {
  const navigate = useNavigate();

  const handleWorkflowComplete = () => {
    navigate('/plan');
  };

  return (
    <WorkflowProvider onComplete={handleWorkflowComplete}>
      <PlanContent />
    </WorkflowProvider>
  );
};

export default Plan;